import { useState } from 'react';
import type { PatientView } from '../../lib/types';
import { HeartMark, Icon } from '../../components/HeartMark';
import { goalLabel } from '@heartcoach/care';
import { fmtDate, fmtSchedule } from '../../lib/format';
import { firstName } from '../../lib/session';

/**
 * Four panels the first time: who prescribed this, the cardiac profile, how it works, and all set.
 * A centred wizard card on a pale ground — everything shown comes from the patient's own record.
 */
export function Onboarding({ view, onDone }: { view: PatientView | null; onDone: () => void }) {
  const [step, setStep] = useState(0);
  const last = 3;

  return (
    <div className="onboard-bg">
      <div className="onboard">
        {step === 0 ? <Welcome view={view} /> : null}
        {step === 1 ? <Profile view={view} /> : null}
        {step === 2 ? <HowItWorks doctor={view?.doctor.name ?? 'your cardiologist'} /> : null}
        {step === 3 ? <AllSet view={view} /> : null}
        <footer className="onboard-foot">
          <button type="button" className="quiet" onClick={() => setStep(Math.max(0, step - 1))} disabled={step === 0}>
            Back
          </button>
          <span className="dots" aria-label={`Step ${step + 1} of 4`}>
            {[0, 1, 2, 3].map((i) => (
              <i key={i} className={i === step ? 'on' : ''} />
            ))}
          </span>
          <button type="button" className="primary" onClick={() => (step === last ? onDone() : setStep(step + 1))}>
            {step === last ? 'Start my coaching →' : step === 2 ? "Let's go →" : 'Next →'}
          </button>
        </footer>
      </div>
    </div>
  );
}

function Welcome({ view }: { view: PatientView | null }) {
  return (
    <div className="onboard-panel hero-panel">
      <div>
        <span className="eyebrow">Your heart has a coach now.</span>
        <h1 className="brand-title" style={{ marginTop: '0.6rem' }}>
          Welcome to HeartCoach
        </h1>
        <p className="hint" style={{ marginTop: '0.75rem', fontSize: '1rem' }}>
          {view ? `${view.doctor.name.replace(/, MD$/, '')} has enrolled you in HeartCoach to support your cardiac care between every appointment.` : 'Your cardiologist has enrolled you in HeartCoach.'}
        </p>
      </div>
      <div>
        <div className="rings">
          <HeartMark size={46} />
        </div>
        <div className="prescribed">
          <span className="eyebrow">
            <span className="asterisk">✱</span> Prescribed by
          </span>
          <strong className="big-name">{view?.doctor.name ?? '…'}</strong>
          <span className="hint">{view?.practiceName ?? ''}</span>
          <span className="badge badge-green" style={{ alignSelf: 'flex-start', marginTop: '0.35rem' }}>
            <Icon name="shield" size={12} /> CVAUSA Verified Practice
          </span>
        </div>
      </div>
    </div>
  );
}

function Profile({ view }: { view: PatientView | null }) {
  return (
    <div className="onboard-panel">
      <span className="eyebrow">Your cardiac profile</span>
      <h1 style={{ margin: '0.5rem 0 0.2rem' }}>
        {view?.profile.name ?? '…'}
        {view?.profile.age ? `, ${view.profile.age}` : ''}
      </h1>
      <span className="hint">Member since {view ? fmtDate(view.profile.memberSince) : '…'}</span>

      <div className="plan-grid">
        <div>
          <span className="eyebrow">Diagnosis</span>
          {view?.profile.diagnoses.map((d) => (
            <div className="row" key={d.code}>
              <span className="bubble bubble-red bubble-sm">
                <Icon name={d.code === 'atrial-fibrillation' ? 'health' : 'star'} size={16} />
              </span>
              <span className="row-main">
                <span className="row-title">{d.label}</span>
                <span className="row-sub">{d.focus}</span>
              </span>
            </div>
          ))}
        </div>
        <div>
          <span className="eyebrow">Medications</span>
          {view?.medications.map((m) => (
            <div className="row" key={m.id}>
              <span className="dot-red" aria-hidden="true" />
              <span className="row-main">
                <span className="row-title">{m.name}</span>
                <span className="row-sub">{fmtSchedule(m.times)}</span>
              </span>
            </div>
          ))}
        </div>
        <div>
          <span className="eyebrow">Care goals</span>
          {view?.goals.map((g, i) => (
            <div className="row" key={i}>
              <span className={`bubble bubble-sm ${g.kind === 'bp' ? 'bubble-red' : g.kind === 'steps' ? 'bubble-green' : 'bubble-purple'}`}>
                <Icon name={g.kind === 'bp' ? 'target' : g.kind === 'steps' ? 'steps' : 'pill'} size={16} />
              </span>
              <span className="row-main">
                <span className="row-title">
                  {`${goalLabel(g).title} ${goalLabel(g).target}`}
                </span>
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function HowItWorks({ doctor }: { doctor: string }) {
  const items: Array<{ icon: Parameters<typeof Icon>[0]['name']; tone: string; title: string; text: string }> = [
    { icon: 'chat', tone: 'red', title: 'Daily AI Coach Check-In', text: 'Your AI coach knows your diagnosis, medications and goals. Personalised guidance every morning in under 60 seconds.' },
    { icon: 'watch', tone: 'blue', title: 'Track Your Health', text: 'Log a reading in about thirty seconds. If your practice turns on device sync, your cuff and watch can fill some of it in for you.' },
    { icon: 'gift', tone: 'gold', title: 'Earn HeartCoins', text: 'Every healthy behaviour earns HeartCoins: medications taken, steps hit, coach sessions completed. You start at zero.' },
    { icon: 'shield', tone: 'green', title: 'Always Clinically Safe', text: `HeartCoach never replaces ${doctor}. Anything urgent is flagged to your care team, who read messages during clinic hours, and the coach always tells you when to call 911 instead of waiting.` },
  ];
  return (
    <div className="onboard-panel">
      <span className="eyebrow">How it works</span>
      <h1 style={{ margin: '0.5rem 0 0' }}>Four things every day</h1>
      <div className="how-grid">
        {items.map((it) => (
          <div className="how" key={it.title}>
            <span className={`bubble bubble-${it.tone}`}>
              <Icon name={it.icon} />
            </span>
            <span className="row-main">
              <strong>{it.title}</strong>
              <span className="hint">{it.text}</span>
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

function AllSet({ view }: { view: PatientView | null }) {
  const name = view ? firstName(view.profile.name) : 'there';
  return (
    <div className="onboard-panel navy-panel">
      <div className="set-grid">
        <div>
          <span className="bubble bubble-xl bubble-red">
            <HeartMark size={40} />
          </span>
          <h1 style={{ margin: '1.1rem 0 0.5rem', fontSize: '2rem' }}>You're all set, {name}.</h1>
          <p className="hint light">
            Your free HeartCoach account is ready. {view?.doctor.name.replace(/, MD$/, '') ?? 'Your doctor'}'s team can see your progress in real time.
          </p>
          <div className="trio">
            <div>
              <Icon name="check" size={22} />
              <strong>Free</strong>
              <span>Forever</span>
            </div>
            <div>
              <Icon name="chat" size={22} />
              <strong>24/7</strong>
              <span>AI Coach</span>
            </div>
            <div>
              <Icon name="star" size={22} />
              <strong>&lt; 1 min</strong>
              <span>Daily</span>
            </div>
          </div>
        </div>
        <div className="card card-navy quote">
          <h2 style={{ fontSize: '1.05rem', margin: '0 0 0.8rem' }}>Three things to know</h2>
          <ul className="plain">
            <li>What you log goes to {view?.practiceName ?? 'your practice'}. That is the point of it: they see trouble early.</li>
            <li>Your coach is an AI. It never changes your medications, and it never replaces {view?.doctor.name.replace(/, MD$/, '') ?? 'your cardiologist'}.</li>
            <li>For chest pain, trouble breathing or fainting, call 911. Do not wait for an answer here.</li>
          </ul>
        </div>
      </div>
    </div>
  );
}
